import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { type Dispatch, useEffect, useState } from 'react';
import UAParser from 'ua-parser-js';

import { useDeviceId } from './useDeviceId';

import { type DeviceData } from '../../.generated/types';
import { type DeviceID } from '../server/deviceId';
import { parseUrlDeviceData } from '../server/utils/urlDeviceData';

interface UseDeviceDataResult {
	deviceData: DeviceData;
	setDeviceData: Dispatch<DeviceData | ((clientData: DeviceData) => DeviceData)>;
}

const currentTimezone = () => {
	dayjs.extend(utc);
	dayjs.extend(timezone);
	return dayjs.tz.guess();
};

const buildDeviceData = (deviceId: DeviceID): DeviceData => {
	const UNKNOWN = 'Unknown';
	const parser = new UAParser();
	const osName = parser.getOS().name;

	const baseDeviceData: DeviceData = {
		device_manufacturer: parser.getDevice().vendor || UNKNOWN,
		device_model: parser.getDevice().model || UNKNOWN,
		timezone: currentTimezone(),
		vendor_id: deviceId || '',
	};

	if (osName) {
		baseDeviceData.os_name = osName;
	}

	const urlData = parseUrlDeviceData();

	return {...baseDeviceData, ...urlData.deviceData};
};

export function useDeviceData(): UseDeviceDataResult {
	const { deviceId } = useDeviceId();
	const [deviceData, setDeviceData] = useState<DeviceData>(() => buildDeviceData(deviceId));

	useEffect(() => {
		if (deviceId) {
			setDeviceData(prev => {
				return {
					...prev,
					vendor_id: deviceId,
				};
			});
		}
	}, [deviceId]);

	return {
		deviceData,
		setDeviceData,
	};
}
